<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useEditorStore } from '@/stores/editor';
import { useAssetsStore } from '@/stores/assets';

const editorStore = useEditorStore();
const assetsStore = useAssetsStore();

const loadingSprite = ref(false);

// Get all frames for the selected sprite
const frames = computed(() => {
    if (editorStore.selectedPaletteSpriteId === null) return [];
    return assetsStore.getFramesForSprite(editorStore.selectedPaletteSpriteId);
});

// Get frame info (texture, dimensions, pivot) for each frame
const frameInfos = computed(() => {
    if (editorStore.selectedPaletteSpriteId === null) return [];

    return frames.value.map(frame => {
        const entry = assetsStore.getAtlasEntry(editorStore.selectedPaletteSpriteId!, frame);
        const texture = assetsStore.getTileTexture(editorStore.selectedPaletteSpriteId!, frame);

        return {
            frame,
            entry,
            texture,
            width: entry?.frame.w ?? 32,
            height: entry?.frame.h ?? 32,
            pivotX: entry?.pivot?.x ?? 0,
            pivotY: entry?.pivot?.y ?? 0,
        };
    });
});

// Currently selected frame
const selectedFrame = computed(() => {
    if (editorStore.objectBrush.length === 0) return null;
    const brush = editorStore.objectBrush[0];
    if (brush.spriteId === editorStore.selectedPaletteSpriteId) {
        return brush.frame;
    }
    return null;
});

// Load sprites when selection changes
watch(() => editorStore.selectedPaletteSpriteId, async (spriteId) => {
    if (spriteId !== null) {
        loadingSprite.value = true;
        try {
            await assetsStore.loadSpritesForIds(new Set([spriteId]));
        } finally {
            loadingSprite.value = false;
        }
    }
}, { immediate: true });

// Select a frame as the object brush
function selectFrame(frame: number): void {
    if (editorStore.selectedPaletteSpriteId === null) return;

    // Clear the brush and set single object
    editorStore.clearObjectBrush();
    editorStore.toggleObjectBrush(0, 0, editorStore.selectedPaletteSpriteId, frame);

    // Also update the legacy single-selection for compatibility
    editorStore.selectObject(editorStore.selectedPaletteSpriteId, frame);
}

// Get the image URL for a frame
function getFrameImageStyle(info: typeof frameInfos.value[0]): Record<string, string> {
    if (!info.entry) return {};

    const entry = assetsStore.getAtlasEntry(editorStore.selectedPaletteSpriteId!, info.frame);
    if (!entry) return {};

    const imagePath = `assets/${entry.image}`;

    return {
        backgroundImage: `url(${imagePath})`,
        backgroundPosition: `-${entry.frame.x}px -${entry.frame.y}px`,
        width: `${entry.frame.w}px`,
        height: `${entry.frame.h}px`,
    };
}
</script>

<template>
    <div class="object-selector" v-if="editorStore.selectedPaletteSpriteId !== null">
        <div class="selector-header">
            <span class="selector-title">Sprite {{ editorStore.selectedPaletteSpriteId }}</span>
            <span class="frame-count">({{ frames.length }} frames)</span>
        </div>

        <div v-if="loadingSprite" class="loading-indicator">
            Loading...
        </div>

        <div v-else class="frames-container">
            <div
                v-for="info in frameInfos"
                :key="info.frame"
                class="frame-item"
                :class="{ selected: selectedFrame === info.frame }"
                @click="selectFrame(info.frame)"
                :title="`Frame ${info.frame} (${info.width}x${info.height})`"
            >
                <div class="frame-preview">
                    <div
                        class="frame-image"
                        :style="getFrameImageStyle(info)"
                    ></div>
                </div>
                <div class="frame-info">
                    <span class="frame-number">#{{ info.frame }}</span>
                    <span class="frame-size">{{ info.width }}x{{ info.height }}</span>
                </div>
            </div>

            <div v-if="frameInfos.length === 0" class="empty-message">
                No frames found
            </div>
        </div>
    </div>
    <div v-else class="no-selection">
        <p>Select a sprite from the palette to view its frames</p>
    </div>
</template>

<style scoped>
.object-selector {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    background: var(--bg-panel);
}

.selector-header {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    font-size: 11px;
    font-weight: 600;
    color: var(--text-secondary);
    text-transform: uppercase;
    letter-spacing: 0.5px;
    background: var(--bg-secondary);
    border-bottom: 1px solid var(--border-color);
    flex-shrink: 0;
}

.selector-title {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.frame-count {
    font-weight: normal;
    text-transform: none;
    white-space: nowrap;
}

.loading-indicator {
    padding: 16px;
    text-align: center;
    color: var(--text-secondary);
    font-size: 12px;
}

.frames-container {
    flex: 1;
    overflow-y: auto;
    padding: 8px;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-content: flex-start;
}

.frame-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 8px;
    background: var(--bg-tertiary);
    border: 2px solid transparent;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.15s ease;
    min-width: 80px;
    max-width: 120px;
}

.frame-item:hover {
    border-color: var(--border-color);
    background: var(--bg-secondary);
}

.frame-item.selected {
    border-color: var(--accent-color);
    background: rgba(99, 102, 241, 0.2);
}

.frame-preview {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 48px;
    max-height: 96px;
    overflow: hidden;
    background:
        linear-gradient(45deg, #2a2a3a 25%, transparent 25%),
        linear-gradient(-45deg, #2a2a3a 25%, transparent 25%),
        linear-gradient(45deg, transparent 75%, #2a2a3a 75%),
        linear-gradient(-45deg, transparent 75%, #2a2a3a 75%);
    background-size: 8px 8px;
    background-position: 0 0, 0 4px, 4px -4px, -4px 0px;
    background-color: #1e1e2e;
    border-radius: 4px;
    padding: 4px;
}

.frame-image {
    image-rendering: pixelated;
    image-rendering: crisp-edges;
    transform-origin: center;
}

.frame-info {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-top: 6px;
    gap: 2px;
}

.frame-number {
    font-size: 11px;
    font-weight: 600;
    color: var(--text-primary);
}

.frame-size {
    font-size: 10px;
    color: var(--text-secondary);
}

.empty-message {
    padding: 16px;
    text-align: center;
    color: var(--text-secondary);
    font-size: 12px;
    width: 100%;
}

.no-selection {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 1;
    padding: 16px;
    color: var(--text-secondary);
    font-size: 12px;
    text-align: center;
}
</style>
